/**
 * Utility functions for adding referral parameters to enrollment URLs
 */

import { getCookie } from 'cookies-next/client';
import { REFERRAL_TOKEN_COOKIE } from './cookies';
import { isBrowser } from './environment';
import { getUrlParseBase } from './url';

const REFERRAL_PAGE_REGEX = /^\/r\/([^/]+)/;

export function isReferralPage(path: string) {
  return REFERRAL_PAGE_REGEX.test(path);
}

export interface EnrollmentTokenSources {
  pathname: string;
  cookieValue: string | undefined;
  queryParams: URLSearchParams;
}

// Pure so the server resolves the same token the client will. Precedence: path, cookie, query.
export function resolveReferralToken({ pathname, cookieValue, queryParams }: EnrollmentTokenSources): string | null {
  const referralMatch = pathname.match(REFERRAL_PAGE_REGEX);
  if (referralMatch && referralMatch[1]) {
    return referralMatch[1];
  }

  if (cookieValue) {
    try {
      // The cookie value might be JSON with a referral_token property
      const parsed = JSON.parse(cookieValue);
      if (parsed.referral_token) {
        return parsed.referral_token;
      }
      // If it's not JSON or doesn't have referral_token, use the value as-is
      return cookieValue;
    } catch {
      // If parsing fails, use the value as-is
      return cookieValue;
    }
  }

  return queryParams.get('ch_rafid') || null;
}

function getReferralToken(): string | null {
  if (!isBrowser()) {
    return null;
  }

  return resolveReferralToken({
    pathname: window.location.pathname,
    cookieValue: getCookie(REFERRAL_TOKEN_COOKIE),
    queryParams: new URLSearchParams(window.location.search),
  });
}

/**
 * Add referral parameters to enrollment URLs. Omit `token` to resolve it from the browser.
 */
export function addReferralParamsToUrl(url: string, token: string | null = getReferralToken()): string {
  // Only add params to URLs containing /enroll
  if (!url.includes('/enroll')) {
    return url;
  }

  if (!token) {
    return url;
  }

  try {
    const urlObj = new URL(url, getUrlParseBase());
    urlObj.searchParams.set('ad', 'ch_raf');
    urlObj.searchParams.set('ch_rafid', token);

    // Return the URL, keeping it relative if it was relative
    if (url.startsWith('/')) {
      return urlObj.pathname + urlObj.search;
    }
    return urlObj.toString();
  } catch {
    // If URL parsing fails, fall back to simple string concatenation
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}ad=ch_raf&ch_rafid=${token}`;
  }
}
